<script setup>
import { WdsButtonPrimary } from '@wds/components';

// ### PROPS
const props = defineProps({
  headingLevel: {
    type: String,
    default: 'h3',
    validator: (val) => ['h2', 'h3'].includes(val),
  },
  links: {
    type: Object,
    required: true,
    validator: (val) => val?.desktop && val?.mobile,
  },
});

// ### COMPOSABLES
const { t } = useI18n();
const { isMobileOrTablet } = useDevice();

// ### COMPUTED
const grabTheAppLink = computed(() => {
  const links = props.links;
  return isMobileOrTablet ? links.mobile : links.desktop;
});
</script>

<template>
  <div class="grab-the-app-banner-container">
    <div class="grab-the-app-banner">
      <div class="banner-image" />
      <component
        :is="headingLevel"
        class="banner-title"
      >
        {{ t('t_OGEVENTSGRABTHEAPPTITLE') }}
      </component>
      <p class="banner-description">
        {{ t('t_OGEVENTSGRABTHEAPPDESCRIPTION') }}
      </p>
      <WdsButtonPrimary
        :text="t('t_OGEVENTSGRABTHEAPPBUTTON')"
        :aria-label="t('t_OGEVENTSGRABTHEAPPBUTTON')"
        class="banner-button"
        reversed
        :href="grabTheAppLink"
        rel="noopener noreferrer"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
$grab-the-app-bg-image-vertical: url('@/assets/images/events/grab-the-app-bg-img-sm.png');
$grab-the-app-bg-image-horizontal: url('@/assets/images/events/grab-the-app-bg-img-wide.png');

.grab-the-app-banner-container {
  container-type: inline-size;
  container-name: grab-the-app;

  .grab-the-app-banner {
    display: grid;
    grid-template-columns: 92px 1fr;
    grid-template-areas:
      'image title'
      'image description'
      'image button';
    gap: 0;
    background: $wds-color-orange-dark;
    border-radius: $wds-border_radius-xl;
    padding: $wds-spacing-l;
    color: $wds-color-white;
    overflow: hidden;

    .banner-image {
      grid-area: image;
      background-image: $grab-the-app-bg-image-vertical;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      margin: -$wds-spacing-l;
    }

    .banner-title {
      grid-area: title;

      @include title-3-bld;
    }

    .banner-description {
      grid-area: description;

      @include body-1-bld;
    }

    .banner-button {
      margin-top: $wds-spacing-s;
    }
  }

  @container grab-the-app (width >= 720px) {
    .grab-the-app-banner {
      gap: 0 $wds-spacing-l;
      grid-template-columns: 200px 1fr min-content;
      grid-template-areas:
        'image title button'
        'image description button';

      .banner-image {
        background-image: $grab-the-app-bg-image-horizontal;
      }

      .banner-button {
        grid-area: button;
        place-self: center end;
      }
    }
  }
}
</style>
